import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Calculator, TrendingUp, AlertCircle } from 'lucide-react';

export const IVFSuccessEstimator = () => {
  const [formData, setFormData] = useState({
    age: '',
    bmi: '',
    amh: '',
    afc: '',
    previousAttempts: '',
    embryoQuality: '',
    endometrialThickness: '',
    stimulationResponse: '',
    maleFactor: '',
    pcos: ''
  });

  const [result, setResult] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateScore = () => {
    let score = 75;
    const insights = [];

    // Age
    const age = parseFloat(formData.age);
    if (age < 30) {
      score += 5;
      insights.push("Younger age is associated with better outcomes");
    } else if (age >= 30 && age <= 34) {
      score += 0;
    } else if (age >= 35 && age <= 37) {
      score -= 10;
      insights.push("Age 35-37 shows moderate impact on success rates");
    } else if (age >= 38 && age <= 40) {
      score -= 20;
      insights.push("Age factor may require additional support strategies");
    } else if (age > 40) {
      score -= 30;
      insights.push("Advanced maternal age requires specialized protocols");
    }

    // BMI
    const bmi = parseFloat(formData.bmi);
    if (bmi >= 18.5 && bmi <= 24.9) {
      score += 3;
      insights.push("Optimal BMI range supports treatment success");
    } else if (bmi >= 25 && bmi <= 29.9) {
      score += 0;
    } else if (bmi >= 30 && bmi <= 34.9) {
      score -= 5;
    } else if (bmi >= 35) {
      score -= 10;
      insights.push("BMI optimization may improve outcomes");
    }

    // AMH
    const amh = parseFloat(formData.amh);
    if (amh > 4) {
      score += 8;
      insights.push("Excellent ovarian reserve indicated");
    } else if (amh >= 2.5 && amh <= 4) {
      score += 4;
      insights.push("Good ovarian reserve");
    } else if (amh >= 1.5 && amh < 2.5) {
      score += 0;
    } else if (amh >= 1 && amh < 1.5) {
      score -= 8;
      insights.push("Lower ovarian reserve may require tailored protocols");
    } else if (amh < 1) {
      score -= 15;
      insights.push("Diminished ovarian reserve requires specialized approach");
    }

    // AFC
    const afc = parseFloat(formData.afc);
    if (afc > 15) {
      score += 8;
    } else if (afc >= 10 && afc <= 15) {
      score += 4;
    } else if (afc >= 6 && afc <= 9) {
      score += 0;
    } else if (afc < 6) {
      score -= 10;
      insights.push("Low antral follicle count may impact response");
    }

    // Previous attempts
    const attempts = parseInt(formData.previousAttempts);
    if (attempts === 0) {
      score += 4;
    } else if (attempts === 1) {
      score += 0;
    } else if (attempts === 2) {
      score -= 5;
    } else if (attempts >= 3) {
      score -= 10;
      insights.push("Multiple previous attempts suggest need for protocol review");
    }

    // Embryo quality
    if (formData.embryoQuality === 'Good') {
      score += 10;
      insights.push("High-quality embryo significantly improves success probability");
    } else if (formData.embryoQuality === 'Average') {
      score += 0;
    } else if (formData.embryoQuality === 'Poor') {
      score -= 12;
      insights.push("Embryo quality optimization strategies recommended");
    }

    // Endometrial thickness
    const endo = parseFloat(formData.endometrialThickness);
    if (endo >= 8 && endo <= 12) {
      score += 5;
      insights.push("Optimal endometrial thickness for implantation");
    } else if (endo >= 7 && endo < 8) {
      score += 0;
    } else if (endo < 7) {
      score -= 10;
      insights.push("Endometrial preparation may need optimization");
    } else if (endo > 12) {
      score -= 3;
    }

    // Stimulation response
    if (formData.stimulationResponse === 'High') {
      score += 6;
      insights.push("Excellent ovarian response to stimulation");
    } else if (formData.stimulationResponse === 'Medium') {
      score += 0;
    } else if (formData.stimulationResponse === 'Low') {
      score -= 10;
      insights.push("Stimulation protocol adjustment may be beneficial");
    }

    // Male factor
    if (formData.maleFactor === 'No') {
      score += 2;
    } else if (formData.maleFactor === 'Yes') {
      score -= 4;
      insights.push("Male factor considerations in treatment plan");
    }

    // PCOS
    if (formData.pcos === 'Yes') {
      score -= 2;
    }

    // Clamp score
    score = Math.max(5, Math.min(80, score));

    // Determine category
    let category = '';
    let categoryColor = '';
    if (score >= 60) {
      category = 'High';
      categoryColor = 'bg-green-100 text-green-800 border-green-200';
    } else if (score >= 35) {
      category = 'Moderate';
      categoryColor = 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else {
      category = 'Low';
      categoryColor = 'bg-orange-100 text-orange-800 border-orange-200';
    }

    setResult({
      score,
      category,
      categoryColor,
      insights: insights.slice(0, 3) // Show top 3 insights
    });
  };

  const isFormValid = () => {
    return formData.age && formData.bmi && formData.amh && formData.afc && 
           formData.previousAttempts !== '' && formData.embryoQuality && 
           formData.endometrialThickness && formData.stimulationResponse && 
           formData.maleFactor && formData.pcos;
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-8 rounded-xl">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-gray-900 mb-3">
            See Predictive Analytics in Action
          </h3>
          <p className="text-gray-600 mb-2">
            👉 This is a simplified demonstration of the type of predictive model we build for IVF clinics, using the same clinical variables collected at first consultation. Enter patient parameters to see how pre-treatment data translates into risk stratification.
          </p>
          <p className="text-sm text-gray-500 italic">
            This dashboard uses simulated data for demonstration purposes
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center space-x-2 mb-6">
                <Calculator className="w-6 h-6 text-blue-600" />
                <h4 className="text-xl font-semibold text-gray-900">Clinical Parameters</h4>
              </div>

              <div className="space-y-4">
                {/* Age */}
                <div>
                  <Label htmlFor="age">Age (years)</Label>
                  <Input
                    id="age"
                    type="number"
                    placeholder="e.g., 32"
                    value={formData.age}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* BMI */}
                <div>
                  <Label htmlFor="bmi">BMI (kg/m²)</Label>
                  <Input
                    id="bmi"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 23.5"
                    value={formData.bmi}
                    onChange={(e) => handleInputChange('bmi', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* AMH */}
                <div>
                  <Label htmlFor="amh">AMH (ng/mL)</Label>
                  <Input
                    id="amh"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 3.2"
                    value={formData.amh}
                    onChange={(e) => handleInputChange('amh', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* AFC */}
                <div>
                  <Label htmlFor="afc">AFC (Antral Follicle Count)</Label>
                  <Input
                    id="afc"
                    type="number"
                    placeholder="e.g., 12"
                    value={formData.afc}
                    onChange={(e) => handleInputChange('afc', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Previous Attempts */}
                <div>
                  <Label htmlFor="attempts">Previous IVF Attempts</Label>
                  <Input
                    id="attempts"
                    type="number"
                    placeholder="e.g., 0"
                    value={formData.previousAttempts}
                    onChange={(e) => handleInputChange('previousAttempts', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Embryo Quality */}
                <div>
                  <Label htmlFor="embryoQuality">Embryo Quality</Label>
                  <Select onValueChange={(value) => handleInputChange('embryoQuality', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select quality" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Good">Good</SelectItem>
                      <SelectItem value="Average">Average</SelectItem>
                      <SelectItem value="Poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Endometrial Thickness */}
                <div>
                  <Label htmlFor="endo">Endometrial Thickness (mm)</Label>
                  <Input
                    id="endo"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 9.5"
                    value={formData.endometrialThickness}
                    onChange={(e) => handleInputChange('endometrialThickness', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Stimulation Response */}
                <div>
                  <Label htmlFor="stimulation">Stimulation Response</Label>
                  <Select onValueChange={(value) => handleInputChange('stimulationResponse', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select response" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Male Factor */}
                <div>
                  <Label htmlFor="maleFactor">Male Factor Infertility</Label>
                  <Select onValueChange={(value) => handleInputChange('maleFactor', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* PCOS */}
                <div>
                  <Label htmlFor="pcos">PCOS</Label>
                  <Select onValueChange={(value) => handleInputChange('pcos', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={calculateScore} 
                  disabled={!isFormValid()}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-4"
                  size="lg"
                >
                  Calculate Estimate
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Result Card */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-blue-50">
            <CardContent className="p-6">
              <div className="flex items-center space-x-2 mb-6">
                <TrendingUp className="w-6 h-6 text-blue-600" />
                <h4 className="text-xl font-semibold text-gray-900">Estimated Outcome</h4>
              </div>

              {result ? (
                <div className="space-y-6">
                  {/* Score Display */}
                  <div className="text-center bg-white rounded-xl p-8 shadow-sm">
                    <div className="text-6xl font-bold text-blue-600 mb-3">
                      {result.score}%
                    </div>
                    <div className="text-lg text-gray-600 mb-4">
                      Estimated Success (Demo)
                    </div>
                    <Badge className={`${result.categoryColor} px-4 py-2 text-base font-semibold border`}>
                      {result.category} Probability
                    </Badge>
                  </div>

                  {/* Insights */}
                  {result.insights.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                      <h5 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <AlertCircle className="w-5 h-5 text-blue-600 mr-2" />
                        Key Insights
                      </h5>
                      <ul className="space-y-3">
                        {result.insights.map((insight, index) => (
                          <li key={index} className="flex items-start space-x-2 text-sm text-gray-700">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0 mt-2" />
                            <span>{insight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Disclaimer */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-xs text-gray-700 leading-relaxed">
                      <strong>Disclaimer:</strong> This tool uses simulated data and simplified logic for demonstration purposes only. It is not medical advice. Actual IVF success depends on many additional factors and should be discussed with qualified healthcare professionals.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calculator className="w-12 h-12 text-blue-600" />
                  </div>
                  <p className="text-gray-600 text-lg">
                    Enter clinical parameters and click "Calculate Estimate" to see predictive modeling in action
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Disclaimer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500 italic">
            This interactive tool demonstrates statistical modeling capabilities. All calculations use simplified algorithms for educational purposes.
          </p>
        </div>
      </div>
    </div>
  );
};
